import type { StudioApi } from '../useTechnicalStudio';
import PivotSection from './PivotSection';
import FibonacciSection from './FibonacciSection';
import StrategySection from './StrategySection';

export type ToolKey = 'pivot' | 'fibonacci' | 'strategy';

const TOOLS: { key: ToolKey; label: string; desc: string; icon: string; accent: string }[] = [
  { key: 'pivot', label: 'Máy tính Pivot Point', desc: 'Hỗ trợ & kháng cự', icon: 'ri-crosshair-2-line', accent: '#10b981' },
  { key: 'fibonacci', label: 'Máy tính Fibonacci', desc: 'Vùng hồi & mở rộng', icon: 'ri-line-chart-line', accent: '#8b5cf6' },
  { key: 'strategy', label: 'Phân tích dữ liệu chiến lược', desc: 'Hiệu suất chiến lược', icon: 'ri-bar-chart-box-line', accent: '#f59e0b' },
];

export default function Studio({
  api,
  active,
  onSelect,
}: {
  api: StudioApi;
  active: ToolKey;
  onSelect: (key: ToolKey) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[256px_minmax(0,1fr)] lg:gap-8">
      {/* Left tool navigation */}
      <nav aria-label="Bộ công cụ phân tích" className="lg:sticky lg:top-28 lg:self-start">
        <p className="mb-3 hidden px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 lg:block">
          Bộ công cụ
        </p>
        <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:gap-1.5 lg:overflow-visible lg:pb-0">
          {TOOLS.map((tool) => {
            const isActive = tool.key === active;
            return (
              <button
                key={tool.key}
                type="button"
                onClick={() => onSelect(tool.key)}
                className={`group relative flex w-auto shrink-0 items-center gap-3 rounded-2xl py-3 pl-4 pr-4 text-left transition-all duration-300 lg:w-full lg:shrink ${
                  isActive ? 'bg-white ring-1 ring-slate-200/80' : 'hover:bg-white/70'
                }`}
              >
                {/* Accent bar for the active tool */}
                <span
                  className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full transition-colors duration-300"
                  style={{ backgroundColor: isActive ? tool.accent : 'transparent' }}
                />
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg transition-colors duration-300 ${
                    isActive ? 'text-white' : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200 group-hover:text-slate-600'
                  }`}
                  style={isActive ? { backgroundColor: tool.accent } : undefined}
                >
                  <i className={tool.icon} />
                </span>
                <span className="min-w-0">
                  <span
                    className={`block whitespace-nowrap text-sm font-semibold leading-tight transition-colors duration-300 lg:whitespace-normal ${
                      isActive ? 'text-slate-900' : 'text-slate-600 group-hover:text-slate-800'
                    }`}
                  >
                    {tool.label}
                  </span>
                  <span
                    className={`mt-0.5 hidden text-xs leading-tight transition-colors duration-300 lg:block ${
                      isActive ? 'text-slate-500' : 'text-slate-400'
                    }`}
                  >
                    {tool.desc}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Tool content — directly beside the navigation, no nested card */}
      <div key={active} className="min-w-0 animate-fade-up">
        {active === 'pivot' && <PivotSection api={api} />}
        {active === 'fibonacci' && <FibonacciSection api={api} />}
        {active === 'strategy' && <StrategySection api={api} />}
      </div>
    </div>
  );
}